import { useCallback, useEffect, useState } from 'react';
import { fetchPlayerCrossStats } from '@/lib/api';
import type { PlayerCrossStatistics } from '@/types/playerCrossStats';

interface UsePlayerCrossStatsResult {
  data: PlayerCrossStatistics | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch + Zod-validate one player's cross-session statistics.
 * Mirrors the shape of `useStatistics` so the page can reuse the same
 * splash/error patterns.
 */
export function usePlayerCrossStats(
  playerId: string,
): UsePlayerCrossStatsResult {
  const [data, setData] = useState<PlayerCrossStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const stats = await fetchPlayerCrossStats(playerId);
      setData(stats);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refetch: load };
}
